import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicOrder } from "api/repository/orders.api";
import { getCurrentClientConfig } from "config/clientConfig";
import { useTranslation } from "context/LanguageContext";
import { getSavedOrder } from "services/orderTrackingStorage";
import OrderPageLayout from "features/orders/components/OrderPageLayout";
import OrderPageHeader from "features/orders/components/OrderPageHeader";
import PackageTrackingLink from "features/orders/components/PackageTrackingLink";
import OrderItemCard from "features/orders/components/OrderItemCard";

const money = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    Number(value) || 0,
  );

export default function OrderTrackingPage() {
  const { orderId } = useParams();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = getSavedOrder(getCurrentClientConfig().tenantId, orderId);
    if (!saved) {
      setError(t("trackingUnavailable"));
      setLoading(false);
      return;
    }

    let active = true;
    getPublicOrder(orderId, saved.trackingToken)
      .then((response) => {
        if (active) setOrder(response?.data ?? response);
      })
      .catch((requestError) => {
        if (!active) return;
        const status = requestError?.response?.status;
        const message = requestError?.response?.data?.message;
        if (status === 401) setError(t("trackingExpired"));
        else if (status === 404) setError(t("orderNotFound"));
        else if (status === 429) setError(t("tooManyRequests"));
        else setError(message || t("trackingLoadFailed"));
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [orderId, t]);

  return (
    <OrderPageLayout
      header={
        <OrderPageHeader
          title={`${t("trackOrder")} #${orderId}`}
          navigation="back"
        />
      }
    >
      {loading && <div className="text-center py-12">{t("loadingOrder")}</div>}
      {error && (
        <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl p-5">
          {error}
        </div>
      )}
      {order && (
        <div className="space-y-5">
          <PackageTrackingLink packageId={order.uPackageId} />
          <section className="bg-white border rounded-xl p-5">
            <h2 className="font-bold text-lg mb-3">{t("orderDetails")}</h2>
            <p>
              {t("customer")}: {order.customerName || "-"}
            </p>
            <p>
              {t("phone")}: {order.customerPhoneExtension || ""}{" "}
              {order.customerPhone || "-"}
            </p>
            <p>
              {t("orderDate")}: {order.createdAt || "-"}
            </p>
            <p>
              {t("deliveryCost")}: {money(order.deliveryCost)}
            </p>

            {order.notes && (
              <p>
                {t("notes")}: {order.notes}
              </p>
            )}
          </section>
          <section className="bg-white border rounded-xl p-5">
            <h2 className="font-bold text-lg mb-3">{t("orderItems")}</h2>
            <div className="space-y-3">
              {(order.items || []).map((item) => (
                <OrderItemCard key={item.id} item={item} />
              ))}
            </div>
            <div className="border-t mt-4 pt-3 flex justify-between font-bold">
              <span>{t("total")}</span>
              <span>{money(order.total)}</span>
            </div>
          </section>
        </div>
      )}
    </OrderPageLayout>
  );
}
